import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "./apiClient";

/**
 * eChannels Tools — CSOB eChannels konzole na `/admin/api/v1/tools/echannels/**`. 21 operací + OAuth2 token panel +
 * cache workflow pending transakcí. Token spravuje backend (subject+scope);
 * klient posílá jen `subject` a volitelně `accessTokenOverride`. Envelope
 * `{httpStatus, success, result, tokenInfo, testTraceId, error}`.
 */

export interface EchannelsMeta {
  enabled: boolean;
  baseUrl?: string;
  signingBaseUrl?: string;
  tokenUrl?: string;
  clientId?: string;
  scope?: string;
  signingScope?: string;
  oauth2Enabled: boolean;
  signingOauth2Enabled: boolean;
}

export interface CachedTransaction {
  guid: string;
  transactionId?: string | null;
  signId?: number | null;
  requestStatusId?: number | null;
  requestTypeId?: number | null;
  ippid?: string | null;
  creationDate?: string | null;
  expirationDate?: string | null;
  secureChannelMessageSignature?: string | null;
  device?: string | null;
  deviceAuthorizationType?: number | null;
  detailLoaded?: boolean;
}

export interface EchannelsTokenInfo {
  scope?: string;
  source?: string; // override | cache | refreshed | new
  expiresIn?: number | null;
  hasRefreshToken?: boolean;
}

export interface EchannelsEnvelope {
  httpStatus?: number;
  success: boolean;
  result?: {
    response?: unknown;
    cachedTransactions?: CachedTransaction[];
    selectedTokenId?: string;
    cachedTransaction?: CachedTransaction;
    generatedSignature?: string;
    usedValidPin?: boolean;
  };
  tokenInfo?: EchannelsTokenInfo;
  testTraceId?: string;
  error?: string;
}

export interface EchannelsExecuteResponse {
  envelope: EchannelsEnvelope;
  securityBlock: boolean;
  rawStatus: number;
}

/** Page-model metadata (jen když je konektor přítomen). */
export function useEchannelsMeta(enabled: boolean) {
  return useQuery({
    queryKey: ["echannels-meta"],
    queryFn: () => apiFetch<EchannelsMeta>("/admin/api/v1/tools/echannels/meta"),
    enabled,
    refetchOnWindowFocus: false,
  });
}

/** Health check activation API. */
export function useEchannelsHealth(enabled: boolean) {
  return useQuery({
    queryKey: ["echannels-health"],
    queryFn: () => apiFetch<{ healthy: boolean; baseUrl?: string; error?: string }>("/admin/api/v1/tools/echannels/health"),
    enabled,
    refetchOnWindowFocus: false,
  });
}

// Seznam tokenů pro výběr zde býval jako useEchannelsTokens() nad
// /admin/api/v1/tools/echannels/tokens — stahoval všechny eChannels tokeny naráz.
// Výběr nově obsluhuje sdílená komponenta TokenPicker nad stránkovaným admin API.

export interface EchannelsTokenExchange {
  success: boolean;
  token?: { access_token: string; token_type: string; expires_in: number; has_refresh_token: boolean };
  error?: string;
}

/** Manuální OAuth2 token exchange (Get Access Token). */
export async function getEchannelsToken(subject: string, scope: string): Promise<EchannelsTokenExchange> {
  const res = await fetch("/admin/api/v1/tools/echannels/get-token", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ subject, scope }),
  });
  const text = await res.text();
  try {
    return JSON.parse(text) as EchannelsTokenExchange;
  } catch {
    return { success: false, error: text || `HTTP ${res.status}` };
  }
}

/** Provede jednu eChannels operaci. Vlastní fetch kvůli rozpoznání security bloku. */
export async function executeEchannelsTool(operation: string, body: Record<string, unknown>): Promise<EchannelsExecuteResponse> {
  const res = await fetch(`/admin/api/v1/tools/echannels/execute/${operation}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  try {
    const envelope = JSON.parse(text) as EchannelsEnvelope;
    const securityBlock = (res.status === 401 || res.status === 403) && envelope.success === undefined;
    return { envelope, securityBlock, rawStatus: res.status };
  } catch {
    return {
      envelope: { success: false, error: text || `HTTP ${res.status}`, httpStatus: res.status },
      securityBlock: res.status === 401 || res.status === 403,
      rawStatus: res.status,
    };
  }
}
